import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import {
  updateUserProfile,
  changePassword,
  uploadAvatar,
} from "../services/users";
import { refreshUserSession } from "../services/auth";
import UserAvatar from "../components/common/UserAvatar";

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile form state
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [email, setEmail] = useState(user?.email || "");

  // Avatar state
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [showAvatarDialog, setShowAvatarDialog] = useState(false);

  // Password change form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Password visibility state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowCurrentPassword = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);

      if (!user) {
        throw new Error("User not authenticated");
      }

      // Update profile via API (which updates Cognito and DynamoDB)
      await updateUserProfile(user.userId, {
        firstName,
        lastName,
        email,
      });

      // Refresh user session from Cognito to get the latest attributes
      const refreshedUser = await refreshUserSession();
      if (refreshedUser) {
        // Update user context with fresh data from Cognito
        updateUser(refreshedUser);
        console.log("Profile updated and session refreshed successfully");
      }

      setSuccess("Profile updated successfully");
    } catch (err) {
      setError("Failed to update profile");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setPasswordError(null);
      setPasswordSuccess(null);

      if (!user) {
        throw new Error("User not authenticated");
      }

      if (newPassword !== confirmPassword) {
        setPasswordError("New passwords do not match");
        return;
      }

      if (newPassword.length < 8) {
        setPasswordError("Password must be at least 8 characters long");
        return;
      }

      setLoading(true);
      await changePassword(user.userId, currentPassword, newPassword);

      // Clear password fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setPasswordSuccess("Password changed successfully");
    } catch (err) {
      setPasswordError(
        "Failed to change password. Please check your current password."
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarEdit = () => {
    setShowAvatarDialog(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setShowAvatarDialog(false);
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile || !user) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Upload avatar to storage and get URL
      const avatarUrl = await uploadAvatar(user.userId, avatarFile);

      // Update the user's avatar URL
      await updateUserProfile(user.userId, {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        familyId: user.familyId,
        role: user.role,
        avatar: avatarUrl,
      });

      // Refresh user session from Cognito to get the latest avatar URL
      const refreshedUser = await refreshUserSession();
      if (refreshedUser) {
        // Update user context with fresh data from Cognito including the new avatar
        updateUser(refreshedUser);
        console.log("Avatar uploaded and user session refreshed successfully");
      } else {
        // Fallback: manually update the current user with the new avatar URL
        updateUser({
          ...user,
          avatar: avatarUrl,
          updatedAt: new Date().toISOString(),
        });
      }

      setSuccess("Avatar uploaded successfully!");
      setAvatarFile(null);
    } catch (err) {
      setError("Failed to upload avatar");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
  };

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case "PARENT":
        return "Parent";
      case "CHILD":
        return "Child";
      case "TEACHER":
        return "Teacher";
      case "COACH":
        return "Coach";
      default:
        return role;
    }
  };

  if (!user) {
    return (
      <Box>
        <Typography variant="h6">User not authenticated</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* User Info Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Box sx={{ mb: 2 }}>
              <UserAvatar
                user={user}
                size={120}
                showEditButton={true}
                onEditClick={handleAvatarEdit}
                avatarFile={avatarFile}
              />
            </Box>

            {avatarFile && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{ mb: 2 }}
              >
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleUploadAvatar}
                >
                  Save Avatar
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRemoveAvatar}
                >
                  Cancel
                </Button>
              </Stack>
            )}

            <Typography variant="h5">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {user.email}
            </Typography>

            <Stack direction="row" spacing={1} justifyContent="center">
              <Chip label={getRoleLabel(user.role)} color="primary" />
              <Chip label={`Family ID: ${user.familyId}`} variant="outlined" />
            </Stack>

            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "background.default",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2">Account Created</Typography>
              <Typography variant="body2">
                {new Date(user.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Edit Profile Form */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Edit Profile
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleProfileUpdate} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Update Profile"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>

            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}

            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {passwordSuccess}
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordChange} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={loading}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowCurrentPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showCurrentPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowNewPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showNewPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowConfirmPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Change Password"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Avatar Upload Dialog */}
      <Dialog
        open={showAvatarDialog}
        onClose={() => setShowAvatarDialog(false)}
      >
        <DialogTitle>Upload Avatar</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose an image file to upload as your avatar.
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<UploadIcon />}
            fullWidth
          >
            Choose Image
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAvatarDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
